<template>
  <!-- START: Scan container - Camera supported -->
  <div id="scanner-container" class="scanner-container" v-if="isVideoDeviceAvailable">
    <!-- START: scanner video area -->
    <div class="scanner">
      <!-- START: Video container -->
      <img ref="image" v-show="isCameraInactive" />
      <canvas id="videoCanvas" width="640" height="480" class="video-area"></canvas>
      <!-- END: Video container -->

      <!-- START: Overlay container with the active video overlay  -->
      <div class="overlay-element" v-if="!isCameraInactive">
        <!-- Displaying header after product scanned -->
        <div v-if="displayHeader" class="overlay-title mt-4 mb-4">
          {{ $t('pleaseScanNextItem') }}
        </div>
      </div>
      <!-- END: Overlay container with the active video overlay  -->
      <div class="inactive-overlay" v-else>
        <button class="inactive-camera-button unstyled-btn">
          <WCCameraSnoozeIcon />
          <div class="text-center mt-2" @click="enableScanner">{{ $t('cameraInactive') }}</div>
        </button>
      </div>
    </div>
    <!-- END: scanner video area -->

    <!-- START: scanner instruction / allow camera notification -->
    <div class="scanner-instruction" v-if="!hasCameraPermission">
      <!-- Scanner instruction default view -->
      <ScanDefaultView :hasCameraPermission="hasCameraPermission" />
    </div>
    <!-- END: scanner instruction / allow camera notification -->
  </div>
  <!-- END: Scan container - Camera supported -->

  <!-- START: Scan container - Camera not supported -->
  <div v-else class="scanner-error-container">
    <!-- Exclamation icon -->
    <font-awesome-icon class="font-size-5xl text-muted mb-3" icon="circle-exclamation" />
    <div>{{ $t('deviceNotSupported') }}</div>
  </div>
  <!-- END: Scan container - Camera not supported -->
</template>

<script>
import MediaDevicesMixin from '@/mixins/MediaDevicesMixin';
import WCCameraSnoozeIcon from '@/components/WCSvgImages/WCCameraSnoozeIcon/WCCameraSnoozeIcon.vue';
import CortexService from '@/services/cortex.service';
import ScanDefaultView from './ScanDefaultView.vue';

export default {
  name: 'WCScanner',
  props: {
    displayHeader: {
      type: Boolean,
      default: false,
    },
    isCameraInactive: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ScanDefaultView,
    WCCameraSnoozeIcon,
  },
  mixins: [MediaDevicesMixin],
  data() {
    return {
      isLoading: false,
      scannerStatus: 'INITIAL',
    };
  },
  mounted() {
    // Initating camera if the camera is supported
    this.init();
  },
  beforeDestroy() {
    // Reset the barcode reading when component destroy
    CortexService.stopScanning();
  },
  methods: {
    async init() {
      await this.createCanvas();
      await CortexService.initialize(this.$configuration.cortexKey);

      if (this.isVideoDeviceAvailable) {
        CortexService.startScanning(this.scanHandler);
      }
    },
    async createCanvas() {
      const canvas = document.getElementById('videoCanvas');
      const ctx1 = canvas.getContext('2d');

      const video = document.createElement('video');
      video.setAttribute('id', 'video');
      video.setAttribute('playsinline', true);
      video.setAttribute('width', '640');
      video.setAttribute('height', '480');
      video.hidden = true;
      document.getElementById('scanner-container').appendChild(video);
      video.addEventListener('play', () => {
        function step() {
          ctx1.drawImage(video, 0, 0, canvas.width, canvas.height);
          requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    },
    enableScanner() {
      this.$emit('scanEnabled');
    },
    scanHandler(scanData) {
      if (scanData?.barcodeData) {
        this.$emit('decode', { code: scanData.barcodeData, format: scanData.symbologyName });
      }
    },
  },
  watch: {
    isCameraInactive() {
      if (this.isCameraInactive && this.scannerStatus !== 'STOP') {
        CortexService.stopScanning();
      } else if (!this.isCameraInactive && this.scannerStatus !== 'START') {
        CortexService.startScanning(this.scanHandler);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.scanner-container {
  position: fixed;
  width: 100%;
  height: 87vh;
  background-color: #ffffff;
  bottom: 0;
}

.scanner {
  height: 100%;
}

.scanner-instruction {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 25%;
  border-radius: 1.25rem 1.25rem 0 0;
  background-color: #ffffff;
}

.scanner-error-container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
}

.overlay-element {
  position: absolute;
  top: 0;
  width: 100%;
  height: 75%;
  background: rgb(142 139 139 / 50%);
  -webkit-clip-path: polygon(
    0% 0%,
    0% 100%,
    3% 100%,
    3% 15%,
    97% 15%,
    97% 60%,
    0% 60%,
    0% 100%,
    100% 100%,
    100% 0%
  );
  clip-path: polygon(
    0% 0%,
    0% 100%,
    3% 100%,
    3% 15%,
    97% 15%,
    97% 60%,
    0% 60%,
    0% 100%,
    100% 100%,
    100% 0%
  );
}
.inactive-overlay {
  position: absolute;
  top: 0;
  width: 100%;
  height: 75%;
  background: rgb(142 139 139 / 50%);
}
.inactive-camera-button {
  width: 75%;
  position: absolute;
  top: 15%;
  left: 50%;
  margin-left: -37.5%;
  padding: rem(25px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: var(--white, $white);
  border-radius: 5px;
  opacity: 0.9;
}

.overlay-title {
  display: flex;
  justify-content: center;
  width: 100%;
  color: #ffffff;
  font-weight: 700;
}

@media screen and (orientation: landscape) {
  .scanner-container {
    height: 80vh;
  }

  .overlay-element {
    position: absolute;
    top: 0;
    width: 100%;
    height: 75%;
    background: rgb(142 139 139 / 50%);
    -webkit-clip-path: polygon(
      0% 0%,
      0% 100%,
      35% 100%,
      35% 20%,
      65% 20%,
      65% 60%,
      0% 60%,
      0% 100%,
      100% 100%,
      100% 0%
    );
    clip-path: polygon(
      0% 0%,
      0% 100%,
      35% 100%,
      35% 20%,
      65% 20%,
      65% 60%,
      0% 60%,
      0% 100%,
      100% 100%,
      100% 0%
    );
  }

  .video-area {
    display: table;
    margin: auto;
    height: 60%;
  }
}

@media screen and (orientation: portrait) {
  .overlay-element {
    position: absolute;
    top: 0;
    width: 100%;
    height: 75%;
    background: rgb(142 139 139 / 50%);
    -webkit-clip-path: polygon(
      0% 0%,
      0% 100%,
      3% 100%,
      3% 15%,
      97% 15%,
      97% 60%,
      0% 60%,
      0% 100%,
      100% 100%,
      100% 0%
    );
    clip-path: polygon(
      0% 0%,
      0% 100%,
      3% 100%,
      3% 15%,
      97% 15%,
      97% 60%,
      0% 60%,
      0% 100%,
      100% 100%,
      100% 0%
    );
  }

  .video-area {
    width: 100%;
  }
}
</style>
